import React, { useState, useEffect, useRef } from 'react';
import {
  Key,
  RefreshCw,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  Save,
  Upload,
  AlertCircle,
  Loader2,
  Shield,
  Server,
  Mail,
  Send,
  ChevronDown,
  ChevronRight,
  FileKey,
  AlertTriangle,
  Trash2,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation, formatCount, type Translation } from '@/i18n';
import { useAppStore } from '@/stores/appStore';
import { apiClient, VaultStatus, SecretsListResponse, CertificateStatus } from '@/utils/apiClient';
import { ExtensionCertificates } from './ExtensionCertificates';
import { toast } from '@/stores/toastStore';
import { envClearHeadline, envClearNotes } from '@/utils/envClearNotes';

/** Map API English status errors to the UI locale (raw string for unknown). */
function vaultStatusErrorLabel(error: string | undefined, t: Translation): string | null {
  if (!error) return null;
  switch (error) {
    // Missing VAULT_ADDR / VAULT_TOKEN (ENABLED alone is not enough).
    case 'Vault client not initialized':
      return t.settings.vaultErrorClientNotInitialized;
    // Vars present but Vault unreachable.
    case 'Could not connect to Vault':
      return t.settings.vaultErrorCouldNotConnect;
    // Vault reachable but token rejected.
    case 'Authentication failed': // secrets_manager writes this string
      return t.settings.vaultErrorAuthFailed;
    case 'Vault is disabled':
      return t.settings.vaultErrorDisabled;
    default:
      return error;
  }
}

// Secret categories and their paths
// NOTE: LLM provider secrets (GigaChat, OpenAI, Anthropic, Ollama) are managed
// exclusively via the "LLM Providers" settings tab.  Only infrastructure and
// service secrets appear here.
const SECRET_CATEGORIES = {
  mcp: {
    label: 'mcpSecrets',
    icon: Server,
    secrets: [
      { path: 'mcp/cluster_base_url', label: 'clusterBaseUrl', env: 'CLUSTER_BASE_URL' },
      { path: 'mcp/cluster_api_token', label: 'clusterApiToken', env: 'CLUSTER_API_TOKEN' },
      { path: 'mcp/environment_api_token', label: 'environmentApiToken', env: 'ENVIRONMENT_API_TOKEN' },
      { path: 'chat/mcp_server_url', label: 'mcpServerUrl', env: 'MCP_SERVER_URL' },
    ]
  },
  email: {
    label: 'emailSecrets',
    icon: Mail,
    secrets: [
      { path: 'chat/smtp_verify_email', label: 'smtpVerifyEmail', env: 'SMTP_VERIFY_EMAIL', type: 'boolean' as const },
      { path: 'chat/smtp_host', label: 'smtpHost', env: 'SMTP_HOST' },
      { path: 'chat/smtp_port', label: 'smtpPort', env: 'SMTP_PORT' },
      { path: 'chat/smtp_user', label: 'smtpUser', env: 'SMTP_USER' },
      { path: 'chat/smtp_password', label: 'smtpPassword', env: 'SMTP_PASSWORD' },
      { path: 'chat/smtp_from', label: 'smtpFrom', env: 'SMTP_FROM' },
      { path: 'chat/smtp_use_tls', label: 'smtpUseTls', env: 'SMTP_USE_TLS', type: 'boolean' as const },
      { path: 'chat/app_base_url', label: 'appBaseUrl', env: 'APP_BASE_URL' },
    ]
  },
  telegram: {
    label: 'telegramSecrets',
    icon: Send,
    secrets: [
      { path: 'telegram/bot_token', label: 'telegramBotToken', env: 'TELEGRAM_BOT_TOKEN' },
    ]
  },
};

export const VaultSettings: React.FC = () => {
  const { t, language } = useTranslation();
  const { vaultRefreshTrigger, syncLLMProvidersFromBackend } = useAppStore();
  
  // State
  const [vaultStatus, setVaultStatus] = useState<VaultStatus | null>(null);
  const [secrets, setSecrets] = useState<SecretsListResponse | null>(null);
  const [certStatus, setCertStatus] = useState<CertificateStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // UI state
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['mcp']));
  const [editingSecret, setEditingSecret] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [showSecrets, setShowSecrets] = useState<Set<string>>(new Set());
  const [savingSecret, setSavingSecret] = useState<string | null>(null);
  const [reloading, setReloading] = useState(false);
  const [uploadingCert, setUploadingCert] = useState(false);
  const [storeInVault, setStoreInVault] = useState(false);
  
  // (reveal endpoint removed — secrets are never sent in plaintext from the server)
  
  // .env secrets state
  const [envSecrets, setEnvSecrets] = useState<Array<{ key: string; in_env: boolean; in_vault: boolean }>>([]);
  const [allInVault, setAllInVault] = useState(false);
  const [envSecretsLoading, setEnvSecretsLoading] = useState(false);
  const [clearConfirm, setClearConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearResult, setClearResult] = useState<{ status: 'success' | 'error'; message: string; details?: string[] } | null>(null);
  const [migratingSecret, setMigratingSecret] = useState<string | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Load data on mount
  useEffect(() => {
    loadAllData();
  }, []);
  
  // Refresh when other components (e.g., LLMProviderSettings) update vault secrets
  useEffect(() => {
    if (vaultRefreshTrigger > 0) {
      // Silently refresh without setting loading state (avoid flicker)
      refreshDataSilently();
    }
  }, [vaultRefreshTrigger]);
  
  const refreshDataSilently = async () => {
    try {
      const [statusRes, secretsRes] = await Promise.all([
        apiClient.getSecretsStatus(),
        apiClient.listSecrets(),
      ]);
      setVaultStatus(statusRes);
      setSecrets(secretsRes);
      
      // Also refresh env secrets status if vault is enabled
      if (statusRes?.vault_enabled) {
        const envRes = await apiClient.getEnvSecretsStatus();
        setEnvSecrets(envRes.secrets);
        setAllInVault(envRes.all_in_vault);
      }
    } catch {
      // Silent refresh — don't set error state
    }
  };
  
  const loadAllData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [statusRes, secretsRes, certRes] = await Promise.all([
        apiClient.getSecretsStatus(),
        apiClient.listSecrets(),
        apiClient.getCertificateStatus()
      ]);
      
      setVaultStatus(statusRes);
      setSecrets(secretsRes);
      setCertStatus(certRes);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };
  
  const toggleShowSecret = (path: string) => {
    const newShow = new Set(showSecrets);
    if (newShow.has(path)) {
      newShow.delete(path);
    } else {
      // Show the masked value (server never returns plaintext)
      newShow.add(path);
    }
    setShowSecrets(newShow);
  };
  
  const startEditing = (path: string, currentValue: string) => {
    setEditingSecret(path);
    setEditValue(currentValue === '***' ? '' : '');
  };
  
  const cancelEditing = () => {
    setEditingSecret(null);
    setEditValue('');
  };
  
  const saveSecret = async (path: string, directValue?: string) => {
    const valueToSave = directValue ?? editValue.trim();
    if (!valueToSave) {
      cancelEditing();
      return;
    }

    setSavingSecret(path);
    try {
      await apiClient.setSecret(path, valueToSave);

      // Hide the field after save so UI shows the fresh masked value
      setShowSecrets(prev => {
        const next = new Set(prev);
        next.delete(path);
        return next;
      });

      // Reload secrets to show updated values
      const secretsRes = await apiClient.listSecrets();
      setSecrets(secretsRes);
      toast.success(t.settings.secretSaved || 'Secret saved');

      if (!directValue) cancelEditing();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to save secret';
      setError(msg);
      toast.error(msg);
    } finally {
      setSavingSecret(null);
    }
  };
  
  /**
   * After reload, fetch backend-initialized providers into the UI store
   * (e.g. Ollama configured via Vault on another device).
   * Logic lives in appStore.syncLLMProvidersFromBackend.
   */
  const handleReloadAll = async () => {
    setReloading(true);
    try {
      await apiClient.reloadAllServices();
      await loadAllData();
      await syncLLMProvidersFromBackend();
      toast.success(t.settings.reloadSuccess || 'Services reloaded');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to reload services';
      setError(msg);
      toast.error(msg);
    } finally {
      setReloading(false);
    }
  };
  
  const handleCertUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setUploadingCert(true);
    try {
      await apiClient.uploadCertificate(file, storeInVault);
      const certRes = await apiClient.getCertificateStatus();
      setCertStatus(certRes);
      toast.success(t.settings.certificateUploaded || 'Certificate uploaded');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to upload certificate';
      setError(msg);
      toast.error(msg);
    } finally {
      setUploadingCert(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  /**
   * Extract the key used in the API response from the full vault path.
   * - mcp/cluster_base_url → cluster_base_url
   * - chat/mcp_server_url → mcp_server_url
   */
  const getResponseKey = (_category: string, path: string): string => {
    return path.split('/')[1]; // e.g., 'cluster_base_url'
  };
  
  const getSecretValue = (category: string, path: string): string => {
    if (!secrets) return '';
    const categoryData = secrets[category as keyof SecretsListResponse];
    if (typeof categoryData === 'object' && categoryData !== null) {
      const key = getResponseKey(category, path);
      return (categoryData as Record<string, string>)[key] || '';
    }
    return '';
  };
  
  // Load .env secrets status when vault is enabled
  const loadEnvSecretsStatus = async () => {
    setEnvSecretsLoading(true);
    try {
      const res = await apiClient.getEnvSecretsStatus();
      setEnvSecrets(res.secrets);
      setAllInVault(res.all_in_vault);
    } catch {
      // Silently ignore — section just won't populate
    } finally {
      setEnvSecretsLoading(false);
    }
  };
  
  useEffect(() => {
    if (vaultStatus?.vault_enabled) {
      loadEnvSecretsStatus();
    }
  }, [vaultStatus?.vault_enabled]);
  
  const handleClearEnvSecrets = async () => {
    setClearing(true);
    setClearResult(null);
    try {
      const res = await apiClient.clearEnvSecrets();
      setClearResult({
        // 'partial': one .env was cleared, another could not be rewritten
        status: res.status === 'partial' ? 'error' : 'success',
        message: envClearHeadline(res, t.settings),
        details: envClearNotes(res, t.settings),
      });
      setClearConfirm(false);
      // Refresh the status
      await loadEnvSecretsStatus();
    } catch (e) {
      setClearResult({
        status: 'error',
        message: e instanceof Error ? e.message : 'Failed to clear .env secrets',
      });
    } finally {
      setClearing(false);
    }
  };
  
  const handleMigrateSecret = async (key: string) => {
    setMigratingSecret(key);
    setClearResult(null);
    try {
      const res = await apiClient.migrateSecretToVault(key);
      setClearResult({
        status: 'success',
        message: t.settings.secretMigrated.replace('{key}', res.key || key),
      });
      // Refresh the status to show updated vault/env state
      await loadEnvSecretsStatus();
      // Also refresh main secrets list
      const secretsRes = await apiClient.listSecrets();
      setSecrets(secretsRes);
    } catch (e) {
      setClearResult({
        status: 'error',
        message: e instanceof Error ? e.message : `Failed to migrate ${key} to Vault`,
      });
    } finally {
      setMigratingSecret(null);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const statusError = vaultStatus?.vault_enabled
    ? vaultStatusErrorLabel(vaultStatus.error, t)
    : null;
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {t.settings.vault || 'Vault Secrets'}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t.settings.vaultDescription || 'Manage secrets and certificates for MCP services'}
          </p>
        </div>
        <button
          onClick={handleReloadAll}
          disabled={reloading}
          className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-md transition-colors"
        >
          {reloading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          {t.settings.reloadAllServices || 'Reload All Services'}
        </button>
      </div>
      
      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700 dark:text-red-300">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              <XCircle className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      
      {/* Vault Status */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Key className="h-5 w-5 text-gray-500" />
            <span className="font-medium text-gray-900 dark:text-white">
              {t.settings.vaultStatus || 'Vault Status'}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            {/* enabled = flag; connected = live health from test_connection. */}
            {vaultStatus?.vault_enabled && vaultStatus?.vault_connected ? (
              <>
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span className="text-green-600 dark:text-green-400">
                  {t.settings.vaultConnected || 'Connected'}
                </span>
              </>
            ) : vaultStatus?.vault_enabled && vaultStatus?.vault_authenticated === false ? (
              <>
                <XCircle className="h-5 w-5 text-red-500" />
                <span className="text-red-600 dark:text-red-400">
                  {t.settings.vaultAuthFailed || 'Authentication Failed'}
                </span>
              </>
            ) : vaultStatus?.vault_enabled && vaultStatus?.error === 'Vault client not initialized' ? (
              <>
                <AlertCircle className="h-5 w-5 text-yellow-500" />
                <span className="text-yellow-700 dark:text-yellow-300">
                  {t.settings.vaultNotConfigured || 'Not configured'}
                </span>
              </>
            ) : vaultStatus?.vault_enabled ? (
              <>
                <XCircle className="h-5 w-5 text-red-500" />
                <span className="text-red-600 dark:text-red-400">
                  {t.settings.vaultDisconnected || 'Disconnected'}
                </span>
              </>
            ) : (
              <>
                <AlertCircle className="h-5 w-5 text-yellow-500" />
                <span className="text-yellow-700 dark:text-yellow-300">
                  {t.settings.vaultNotEnabled || 'Vault Not Enabled'}
                </span>
              </>
            )}
          </div>
        </div>
        {vaultStatus?.vault_address && vaultStatus?.vault_enabled && (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {vaultStatus.vault_address}
          </p>
        )}
        {statusError && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">
            {statusError}
          </p>
        )}
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t.settings.secretSource}:{' '}
          {secrets?.source === 'vault'
            ? t.settings.secretSourceVault
            : secrets?.source === 'environment'
              ? t.settings.secretSourceEnvironment
              : t.settings.secretSourceUnknown}
        </p>
      </div>
      
      {/* Secrets Management */}
      <div className="space-y-4">
        <h4 className="text-md font-medium text-gray-900 dark:text-white">
          {t.settings.secretsManagement || 'Secrets'}
        </h4>

        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t.settings.llmProvidersHint || 'LLM provider credentials (GigaChat, OpenAI, Anthropic, Ollama) are managed in the LLM Providers tab. This section covers infrastructure and service secrets only.'}
        </p>

        {Object.entries(SECRET_CATEGORIES).map(([categoryKey, category]) => {
          const Icon = category.icon;
          const isExpanded = expandedCategories.has(categoryKey);
          
          return (
            <div
              key={categoryKey}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
            >
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(categoryKey)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <Icon className="h-5 w-5 text-gray-500" />
                  <span className="font-medium text-gray-900 dark:text-white">
                    {t.settings[category.label as keyof typeof t.settings] || category.label}
                  </span>
                </div>
                {isExpanded ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </button>
              
              {/* Category Content */}
              {isExpanded && (
                <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-4">
                  {category.secrets.map((secret) => {
                    const value = getSecretValue(categoryKey, secret.path);
                    const isEditing = editingSecret === secret.path;
                    const isSaving = savingSecret === secret.path;
                    const isVisible = showSecrets.has(secret.path);
                    const isBoolean = 'type' in secret && secret.type === 'boolean';
                    const boolValue = isBoolean ? (value === 'true') : false;

                    return (
                      <div key={secret.path} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t.settings[secret.label as keyof typeof t.settings] || secret.label}
                          </label>
                          <span className="text-xs text-gray-500">{secret.env}</span>
                        </div>

                        {isBoolean ? (
                          /* Boolean toggle — click to flip, saves immediately */
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => saveSecret(secret.path, boolValue ? 'false' : 'true')}
                              disabled={isSaving}
                              className="flex items-center space-x-2 focus:outline-none disabled:opacity-50"
                              title={boolValue ? t.settings.clickToDisable : t.settings.clickToEnable}
                            >
                              {isSaving ? (
                                <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                              ) : boolValue ? (
                                <ToggleRight className="h-8 w-8 text-green-500" />
                              ) : (
                                <ToggleLeft className="h-8 w-8 text-gray-400" />
                              )}
                              <span className={`text-sm font-medium ${boolValue ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
                                {boolValue ? t.settings.secretEnabled : t.settings.secretDisabled}
                              </span>
                            </button>
                          </div>
                        ) : isEditing ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type={isVisible ? 'text' : 'password'}
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              placeholder={`Enter new ${secret.label}`}
                              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                              autoFocus
                            />
                            <button
                              onClick={() => toggleShowSecret(secret.path)}
                              className="p-2 text-gray-400 hover:text-gray-600"
                            >
                              {isVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                            <button
                              onClick={() => saveSecret(secret.path)}
                              disabled={isSaving || !editValue.trim()}
                              className="px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-md"
                            >
                              {isSaving ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Save className="h-4 w-4" />
                              )}
                            </button>
                            <button
                              onClick={cancelEditing}
                              className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded-md"
                            >
                              {t.common.cancel || 'Cancel'}
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <div className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-md">
                              <span className="text-sm text-gray-600 dark:text-gray-300 font-mono">
                                {isVisible
                                  ? (value || <span className="text-gray-400 italic">{t.settings.secretNotConfigured}</span>)
                                  : (value ? '••••••••' : <span className="text-gray-400 italic">{t.settings.secretNotConfigured}</span>)
                                }
                              </span>
                            </div>
                            <button
                              onClick={() => toggleShowSecret(secret.path)}
                              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                              title={isVisible ? 'Hide' : 'Show masked value'}
                            >
                              {isVisible ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                            <button
                              onClick={() => startEditing(secret.path, value)}
                              className="px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-700"
                            >
                              {t.settings.updateSecret || 'Update'}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Certificate Management */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center space-x-3 mb-4">
          <Shield className="h-5 w-5 text-gray-500" />
          <span className="font-medium text-gray-900 dark:text-white">
            {t.settings.certificates || 'Certificates'}
          </span>
        </div>
        
        {/* Certificate Status */}
        {certStatus && (
          <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t.settings.certificateStatus || 'Certificate Status'}
              </span>
              {certStatus.has_certificate ? (
                certStatus.valid ? (
                  <span className="flex items-center text-green-600 dark:text-green-400">
                    <CheckCircle className="h-4 w-4 mr-1" />
                    {t.settings.certificateValid || 'Valid'}
                  </span>
                ) : (
                  <span className="flex items-center text-red-600 dark:text-red-400">
                    <XCircle className="h-4 w-4 mr-1" />
                    {t.settings.certificateExpired || 'Expired'}
                  </span>
                )
              ) : (
                <span className="text-gray-500">
                  {t.settings.noCertificate || 'No certificate'}
                </span>
              )}
            </div>
            
            {certStatus.has_certificate && (
              <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                {certStatus.expiry_date && (
                  <p>
                    {t.settings.certificateExpiry || 'Expires'}: {new Date(certStatus.expiry_date).toLocaleDateString()}
                    {certStatus.days_remaining !== undefined && (
                      <span className={clsx(
                        'ml-2',
                        certStatus.days_remaining < 7 ? 'text-red-500' :
                        certStatus.days_remaining < 30 ? 'text-yellow-500' : 'text-green-500'
                      )}>
                        ({formatCount(language, certStatus.days_remaining, t.settingsCounts.daysRemaining)})
                      </span>
                    )}
                  </p>
                )}
                {certStatus.source && (
                  <p>
                    {t.settings.certificateSource}:{' '}
                    {certStatus.source === 'vault'
                      ? t.settings.secretSourceVault
                      : t.settings.certificateSourceFile}
                    {certStatus.file_path && ` (${certStatus.file_path})`}
                  </p>
                )}
                {certStatus.max_api_calls && (
                  <p>{t.settings.maxApiCalls}: {certStatus.max_api_calls.toLocaleString()}</p>
                )}
                {/* STORY-485: whether the MCP has this certificate in force is
                    the MCP's own answer, not the dates of the file; "other"
                    when the MCP runs another one (loaded before an upload, or
                    when this one is in Vault, which the MCP does not read) */}
                {certStatus.mcp_status && (
                  <p data-testid="certificate-mcp-status">
                    {t.settings.certificateInMcp}:{' '}
                    <span className={clsx(
                      certStatus.mcp_status === 'active' ? 'text-green-600 dark:text-green-400' :
                      certStatus.mcp_status === 'inactive' ? 'text-red-600 dark:text-red-400' :
                      'text-yellow-600 dark:text-yellow-400'
                    )}>
                      {certStatus.mcp_status === 'active' ? t.settings.certActive :
                       certStatus.mcp_status === 'inactive' ? t.settings.certInactive :
                       certStatus.mcp_status === 'other' ? t.settings.certOtherInMcp :
                       t.settings.certStatusUnknown}
                    </span>
                    {certStatus.mcp_status === 'inactive' && certStatus.mcp_refusal && (
                      <span> ({certStatus.mcp_refusal})</span>
                    )}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Upload Certificate */}
        <div className="space-y-3">
          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={storeInVault}
                onChange={(e) => setStoreInVault(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {t.settings.storeInVault || 'Store in Vault '}
              </span>
            </label>
            <span className="text-xs text-gray-500">
              {storeInVault 
                ? (t.settings.vaultStorageHint || 'Certificate will be stored in Vault KV')
                : (t.settings.fileStorageHint || 'Certificate will be saved to filesystem')
              }
            </span>
          </div>
          
          <div className="flex items-center space-x-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pem"
              onChange={handleCertUpload}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingCert}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded-md transition-colors"
            >
              {uploadingCert ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Upload className="mr-2 h-4 w-4" />
              )}
              {t.settings.uploadCertificate || 'Upload Certificate'}
            </button>
          </div>
        </div>
      </div>

      {/* Extension Certificates */}
      <ExtensionCertificates />

      {/* Environment File Security */}
      {vaultStatus?.vault_enabled && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center space-x-3 mb-4">
            <FileKey className="h-5 w-5 text-gray-500" />
            <span className="font-medium text-gray-900 dark:text-white">
              🔒 {t.settings.envFileSecurity}
            </span>
          </div>
          
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {t.settings.envFileSecurityHint}
          </p>
          
          {/* Secrets checklist */}
          {envSecretsLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
            </div>
          ) : (
            <div className="space-y-2 mb-4">
              {envSecrets.map((s) => (
                <div
                  key={s.key}
                  className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-md"
                >
                  <span className="text-sm font-mono text-gray-700 dark:text-gray-300">
                    {s.key}
                  </span>
                  <div className="flex items-center space-x-3 text-xs">
                    {/* Vault status */}
                    <span className={clsx(
                      'flex items-center',
                      s.in_vault ? 'text-green-600 dark:text-green-400' : 'text-gray-400'
                    )}>
                      {s.in_vault ? (
                        <CheckCircle className="h-3.5 w-3.5 mr-1" />
                      ) : (
                        <XCircle className="h-3.5 w-3.5 mr-1" />
                      )}
                      Vault
                    </span>
                    {/* .env status */}
                    <span className={clsx(
                      'flex items-center',
                      s.in_env ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'
                    )}>
                      {s.in_env ? (
                        <AlertTriangle className="h-3.5 w-3.5 mr-1" />
                      ) : (
                        <CheckCircle className="h-3.5 w-3.5 mr-1" />
                      )}
                      .env
                    </span>
                    {/* Add to Vault button — show when secret is in .env but not in vault */}
                    {s.in_env && !s.in_vault && (
                      <button
                        onClick={() => handleMigrateSecret(s.key)}
                        disabled={migratingSecret === s.key}
                        className="flex items-center px-2 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded transition-colors"
                        title={t.settings.addToVault}
                      >
                        {migratingSecret === s.key ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <>
                            <Upload className="h-3 w-3 mr-1" />
                            {t.settings.addToVault}
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Clear result message */}
          {clearResult && (
            <div className={clsx(
              'mb-4 p-3 rounded-md text-sm',
              clearResult.status === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
                : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
            )}>
              <div className="flex items-center">
                {clearResult.status === 'success' ? (
                  <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                ) : (
                  <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                )}
                {clearResult.message}
              </div>
              {clearResult.details && clearResult.details.length > 0 && (
                <ul className="mt-2 ml-6 list-disc space-y-1">
                  {clearResult.details.map((d) => (
                    <li key={d}>{d}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          
          {/* Hint or action */}
          {!allInVault && !envSecretsLoading && (
            <p className="text-sm text-yellow-600 dark:text-yellow-400 flex items-center">
              <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0" />
              {t.settings.setAllSecretsHint}
            </p>
          )}
          
          {/* Confirmation inline dialog */}
          {clearConfirm ? (
            <div className="mt-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-700 dark:text-red-300 mb-3">
                {t.settings.clearEnvConfirm}
              </p>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleClearEnvSecrets}
                  disabled={clearing}
                  className="flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-md transition-colors"
                >
                  {clearing ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="mr-2 h-4 w-4" />
                  )}
                  {t.settings.confirmClear}
                </button>
                <button
                  onClick={() => setClearConfirm(false)}
                  disabled={clearing}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded-md transition-colors"
                >
                  {t.common.cancel || 'Cancel'}
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => { setClearResult(null); setClearConfirm(true); }}
              disabled={!allInVault || envSecretsLoading}
              className="mt-3 flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t.settings.clearEnvSecrets}
            </button>
          )}
        </div>
      )}
      
      {/* Vault Disconnected / Auth Failed / Not Configured Warning */}
      {vaultStatus?.vault_enabled && !vaultStatus?.vault_connected && (() => {
        const notConfigured = vaultStatus.error === 'Vault client not initialized';
        const authFailed = vaultStatus.vault_authenticated === false;
        return (
          <div className={`rounded-lg p-4 border ${notConfigured ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'}`}>
            <div className="flex items-start">
              {notConfigured
                ? <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5 mr-3" />
                : <XCircle className="h-5 w-5 text-red-500 mt-0.5 mr-3" />}
              <div>
                <h4 className={`text-sm font-medium ${notConfigured ? 'text-yellow-800 dark:text-yellow-200' : 'text-red-800 dark:text-red-200'}`}>
                  {notConfigured
                    ? (t.settings.vaultNotConfigured || 'Not configured')
                    : authFailed
                      ? (t.settings.vaultAuthFailed || 'Authentication Failed')
                      : (t.settings.vaultDisconnected || 'Disconnected')}
                </h4>
                <p className={`mt-1 text-sm ${notConfigured ? 'text-yellow-700 dark:text-yellow-300' : 'text-red-700 dark:text-red-300'}`}>
                  {notConfigured
                    ? (t.settings.vaultNotConfiguredHint || 'Set VAULT_ADDR and VAULT_TOKEN, then restart the service.')
                    : authFailed
                      ? (t.settings.vaultAuthFailedHint || 'Vault is reachable but rejected the token. Update VAULT_TOKEN and restart the service.')
                      : (t.settings.vaultDisconnectedHint || 'Vault is enabled but could not be reached. Check Vault status and restart the service to reconnect.')}
                </p>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Vault Not Enabled Warning */}
      {!vaultStatus?.vault_enabled && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5 mr-3" />
            <div>
              <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                {t.settings.vaultNotEnabled || 'Vault Not Enabled'}
              </h4>
              <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                {t.settings.vaultNotEnabledHint || 
                  'Secrets are loaded from environment variables. To enable Vault, set VAULT_ENABLED=true and configure VAULT_ADDR and VAULT_TOKEN.'
                }
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
